import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, fetchData, postData, extractApiError } from '../services/api';
import { PATH_SEGMENTS, route } from '../config/paths';
import { MomentCategoryResponse } from '../types/api';
import { config } from '../config';
import { 
  EvalFormData, 
  EvalFormDataResponse, 
  EFSectionData, 
  EFQuestionData, 
  EFQuestionOptionData,
  EFQuestionMomentData,
  QuestionType,
  OptionType,
  MomentOperation,
  ResourceState,
  EFErrorResponse
} from '../types/evalform';
import '../styles/screens/EvaluationForm.css';
import { useLayout } from '../contexts/LayoutContext';
import { useToast } from '../contexts/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import { scrollToFirstError } from '../utils/scrollToError';
import MomentExpression from '../components/MomentExpression';
import AutoCompleteDropdown from '../components/AutoCompleteDropdown';
import MomentCategoryDropdown from '../components/MomentCategoryDropdown';
import FormPreviewPanel from '../components/FormPreviewPanel';
import ConfirmationModal, { ConfirmationModalConfig } from '../components/common/ConfirmationModal';

interface MomentOperationInterface {
  id: number;
  name: string;
  description: string;
}

interface ResourceStateResponse {
  states: ResourceState[];
}

interface BooleanResponse {
  booleans: Boolean[];
}

interface MomentOperationResponse {
  operations: MomentOperationInterface[];
}

interface MomentItem {
  id: number;
  name: string;
  description?: string;
  mmt_category_id?: number;
}

interface MomentCategory {
  id: number;
  name: string;
  description?: string;
}

interface MomentsResponse {
  moments: MomentItem[];
}

interface MomentCategoriesResponse {
  categories: MomentCategory[];
}

const EvaluationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { updateHeaderProps } = useLayout();
  const { showToast } = useToast();
  const saveButtonState = useButtonState('Save', 'Saving...', 'Saved ✅');
  const submitButtonState = useButtonState('Submit', 'Submitting...', 'Submitted ✅');
  const formNameSaveState = useButtonState('Save', 'Saving...', 'Saved ✅');
  
  // Track button states for sections (simple loading/success states)
  const [sectionSaveStates, setSectionSaveStates] = useState<{[key: number]: {isLoading: boolean, isSuccess: boolean}}>({});
  
  // Helper to update section save state
  const updateSectionSaveState = (index: number, updates: Partial<{isLoading: boolean, isSuccess: boolean}>) => {
    setSectionSaveStates(prev => ({
      ...prev,
      [index]: { ...prev[index], isLoading: false, isSuccess: false, ...updates }
    }));
  };

  // Add the normalizeEvalFormData function at the top of the component
  const normalizeEvalFormData = (evalform: EvalFormData): EvalFormData => {
    return {
      ...evalform,
      sections: (evalform.sections || [])
        .map(section => ({
          ...section,
          questions: (section.questions || [])
            .map(question => {
              // Get type_id as a number
              const typeId = Number(question.type_id);
              const validTypeId = (typeId === QuestionType.MOMENT || typeId === QuestionType.PROMPT)
                ? typeId
                : QuestionType.MOMENT;

              const optionTypeId = Number(question.option_type_id);
              const validOptionTypeId = [OptionType.YES_NO, OptionType.GRADING, OptionType.MULTI_CHOICE].includes(optionTypeId)
                ? optionTypeId
                : OptionType.YES_NO;

              return {
                ...question,
                type_id: validTypeId,
                option_type_id: validOptionTypeId,
                options: question.options
                  ? [...question.options].sort((a, b) => a.order - b.order)
                  : [],
                moments: question.moments
                  ? [...question.moments].sort((a, b) => a.order - b.order)
                  : []
              };
            })
            .sort((a, b) => a.order - b.order)
        }))
        .sort((a, b) => a.order - b.order)
    };
  };

  // Calculate question points based on option type
  const calculateQuestionPoints = (question: EFQuestionData): number => {
    const options = question.options || [];
    if (Number(question.option_type_id) === OptionType.MULTI_CHOICE) {
      return options.reduce((sum, option) => sum + option.points, 0);
    }
    // For YES_NO and GRADING questions, the question points are the maximum points among options
    // For YES_NO questions, this effectively means the "Yes" option points become the total question points
    return options.length > 0 ? Math.max(...options.map(option => option.points)) : 0;
  };

  // Update all points for all sections and questions in formData
  const updateAllPoints = (dataToUpdate: EvalFormData): EvalFormData => {
    const updatedSections = dataToUpdate.sections.map(section => {
      const updatedQuestions = section.questions.map(question => ({
        ...question,
        points: calculateQuestionPoints(question)
      }));
      
      return {
        ...section,
        questions: updatedQuestions,
        points: updatedQuestions.reduce((sum, question) => sum + (question.points || 0), 0)
      };
    });
    
    return {
      ...dataToUpdate,
      sections: updatedSections
    };
  };

  const [formData, setFormData] = useState<EvalFormData>({
    name: 'QA Evaluation Form',
    points: 0,
    description: '',
    state_id: ResourceState.NEW,
    new_state_id: ResourceState.IN_PROGRESS,
    is_shared_id: 1,
    sections: []
  });
  const [states, setStates] = useState<ResourceState[]>([]);
  const [booleans, setBooleans] = useState<Boolean[]>([]);
  const [operations, setOperations] = useState<MomentOperationInterface[]>([]);
  const [moments, setMoments] = useState<MomentItem[]>([]);
  const [momentCategories, setMomentCategories] = useState<MomentCategory[]>([]);
  
  // Track if static data has been loaded to avoid reloading on every mount
  const staticDataLoadedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [originalFormName, setOriginalFormName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const sectionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const questionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const formEditorRef = useRef<HTMLDivElement>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: number]: boolean }>({});
  const [editingSectionName, setEditingSectionName] = useState<{index: number, value: string, original: string} | null>(null);
  
  // Section modal state (for both add and duplicate)
  interface SectionModalState {
    isOpen: boolean;
    mode: 'add' | 'duplicate';
    sectionIndex: number | null; // null for add, section index for duplicate
    sectionName: string;
    errors: {[key: string]: string};
  }
  
  const [sectionModal, setSectionModal] = useState<SectionModalState>({
    isOpen: false,
    mode: 'add',
    sectionIndex: null,
    sectionName: '',
    errors: {}
  });
  
  const sectionModalSaveState = useButtonState('Save', 'Saving...', 'Saved ✅');
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<ConfirmationModalConfig>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger'
  });
  
  // Question modal state - consolidated into single object
  interface QuestionModalState {
    isOpen: boolean;
    question: EFQuestionData | null;
    sectionIndex: number | null;
    questionIndex: number | null;
    isNew: boolean;
    errors: {[key: string]: string};
    momentRowCategories: {[key: string]: number};
  }
  
  const [modalState, setModalState] = useState<QuestionModalState>({
    isOpen: false,
    question: null,
    sectionIndex: null,
    questionIndex: null,
    isNew: false,
    errors: {},
    momentRowCategories: {}
  });
  
  const questionModalSaveState = useButtonState('Save', 'Saving...', 'Saved ✅');

  // Helper function to update modal state
  const updateModalState = (updates: Partial<QuestionModalState>) => {
    setModalState(prev => ({ ...prev, ...updates }));
  };

  const getFieldError = (fieldName: string) => {
    return fieldErrors[fieldName] || '';
  };

  const validateForm = (): boolean => {
    const newFieldErrors: {[key: string]: string} = {};
    let isValid = true;

    // Validate form name
    if (!formData.name.trim()) {
      newFieldErrors['name'] = 'Form name is required';
      isValid = false;
    }

    // Check for duplicate section names
    const sectionNames = new Map<string, number>();
    formData.sections.forEach((section) => {
      const name = section.name.trim().toLowerCase();
      if (sectionNames.has(name)) {
        newFieldErrors[`section-${section.order}-name`] = 'Section name must be unique';
        isValid = false;
      } else {
        sectionNames.set(name, section.order);
      }
    });

    // Validate sections
    formData.sections.forEach((section) => {
      // Validate section name
      if (!section.name.trim()) {
        newFieldErrors[`section-${section.order}-name`] = 'Section name is required';
        isValid = false;
      }

      // Check for duplicate question names within the same section
      const questionNames = new Map<string, number>();
      section.questions.forEach((question) => {
        const name = question.description.trim().toLowerCase();
        if (questionNames.has(name)) {
          newFieldErrors[`section-${section.order}-question-${question.order}-description`] = 'Question name must be unique within the section';
          isValid = false;
        } else {
          questionNames.set(name, question.order);
        }
      });

      // Validate questions
      section.questions.forEach((question) => {
        // Validate question description
        if (!question.description.trim()) {
          newFieldErrors[`section-${section.order}-question-${question.order}-description`] = 'Question description is required';
          isValid = false;
        }

        // Validate prompt for prompt type questions
        if (question.type_id === QuestionType.PROMPT && !question.prompt?.trim()) {
          newFieldErrors[`section-${section.order}-question-${question.order}-prompt`] = 'Prompt is required for prompt type questions';
          isValid = false;
        }

        // Validate moments for moment type questions
        if (question.type_id === QuestionType.MOMENT) {
          if (!question.moments || question.moments.length === 0) {
            newFieldErrors[`section-${section.order}-question-${question.order}-moments`] = 'At least one moment is required for moment type questions';
            isValid = false;
          } else {
            question.moments.forEach((moment) => {
              if (!moment.moment_id) {
                newFieldErrors[`section-${section.order}-question-${question.order}-moment-${moment.order}-moment`] = 'Moment is required';
                isValid = false;
              }
              // Check if this is the last moment (by order)
              const isLastMoment = moment.order === Math.max(...(question.moments ? question.moments.map(m => m.order) : [0]));
              if (isLastMoment && moment.operation_id !== MomentOperation.NONE) {
                newFieldErrors[`section-${section.order}-question-${question.order}-moment-${moment.order}-operation`] = 'Select NONE for the last moment';
                isValid = false;
              } else if (!isLastMoment && moment.operation_id === MomentOperation.NONE) {
                newFieldErrors[`section-${section.order}-question-${question.order}-moment-${moment.order}-operation`] = 'Select an operation';
                isValid = false;
              }
            });
          }
        }
        // Option validation example (add if you want to validate option descriptions)
        if (question.options && question.options.length > 0) {
          question.options.forEach((option) => {
            if (!option.description || !option.description.trim()) {
              newFieldErrors[`section-${section.order}-question-${question.order}-option-${option.order}-description`] = 'Option description is required';
              isValid = false;
            }
          });
        }
      });
    });

    setFieldErrors(newFieldErrors);
    return isValid;
  };

  const clearFieldError = (fieldName: string) => {
    console.log('clearFieldError: fieldName = ', fieldName);
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  // Use a ref to prevent duplicate calls from React StrictMode double renders
  const hasLoadedRef = useRef<string | null>(null);

  useEffect(() => {
    const currentId = id || 'new';
    
    // Skip if we've already loaded for this id
    if (hasLoadedRef.current === currentId) {
      return;
    }
    
    hasLoadedRef.current = currentId;
    
    const loadData = async () => {
      try {
        setLoading(true);
        if (id && id !== '') {
          const formResponse = await fetchData<EvalFormDataResponse>(api(PATH_SEGMENTS.EVALFORM, id));
          const normalizedData = normalizeEvalFormData(formResponse.evalform);
          const dataWithPoints = updateAllPoints(normalizedData);
          setFormData(dataWithPoints);
        } else {
          const response = await postData<EvalFormDataResponse>(api(PATH_SEGMENTS.EVALFORM), {});
          const normalizedData = normalizeEvalFormData(response.evalform);
          const dataWithPoints = updateAllPoints(normalizedData);
          setFormData(dataWithPoints);
          if (response.evalform.id) {
            navigate(route(PATH_SEGMENTS.EVALFORM, response.evalform.id));
          }
        }
        // Load static data only if not already loaded (these rarely change)
        if (!staticDataLoadedRef.current) {
        // Load resource states
        try {
          const statesResponse = await fetchData<ResourceStateResponse>(api(PATH_SEGMENTS.RESOURCE_STATE));
          setStates(statesResponse.states || []);
        } catch (err) {
          setStates([]);
        }

        // Load booleans
        try {
          const booleansResponse = await fetchData<BooleanResponse>(api(PATH_SEGMENTS.BOOLEAN));
          setBooleans(booleansResponse.booleans || []);
        } catch (err) {
          console.error("Error loading booleans:", err);
          setBooleans([]);
        }
        
        // Load operations
        try {
          const operationsResponse = await fetchData<MomentOperationResponse>(api(PATH_SEGMENTS.MOMENT_OPERATION));
          setOperations(operationsResponse.operations || []);
        } catch (err) {
          console.error("Error loading operations:", err);
          setOperations([]);
        }
        
        // Load moments
        try {
          const momentsResponse = await fetchData<MomentsResponse>(`${api(PATH_SEGMENTS.MOMENT)}/?all_results=true`);
          console.log("momentsResponse", momentsResponse.moments);
          setMoments(momentsResponse.moments || []);
        } catch (err) {
          console.error("Error loading moments:", err);
          setMoments([]);
        }
        
        // Load moment categories - Handle case if API doesn't exist yet
        try {
          const categoriesResponse = await fetchData<MomentCategoryResponse>(`${api(PATH_SEGMENTS.MOMENT, PATH_SEGMENTS.CATEGORY)}/?all_results=true`);
          // Map MomentCategoryItem to MomentCategory format for compatibility
          const mappedCategories = (categoriesResponse.mmt_categories || []).map(item => ({
            id: item.id,
            name: item.name,
            description: item.description
          }));
          setMomentCategories(mappedCategories);
        } catch (err) {
          console.error("Error loading moment categories:", err);
          setMomentCategories([]);
          }
          
          staticDataLoadedRef.current = true;
        }

        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };
    loadData();
  }, [id, navigate]);

  useEffect(() => {
    // Update header props only when id changes
    let pageTitle = 'Edit Evaluation Form';
    if (formData.state_id === ResourceState.NEW) {
      pageTitle = 'New Evaluation Form';
    } else if (!isFormEditable()) {
      pageTitle = 'View Evaluation Form';
    }
    
    updateHeaderProps({
      pageTitle,
      showInteractionExId: false,
    });
    
    // This effect should only run once on mount and when id changes
  }, [id, formData.state_id]);




  
  const toggleFormActive = () => {
    let newStateId = formData.state_id;
    
    if (formData.state_id === ResourceState.INACTIVE_IN_PROGRESS) {
      newStateId = ResourceState.IN_PROGRESS;
    } else if (formData.state_id === ResourceState.IN_PROGRESS) {
      newStateId = ResourceState.INACTIVE_IN_PROGRESS;
    } else if (formData.state_id === ResourceState.COMPLETED) {
      newStateId = ResourceState.INACTIVE_COMPLETED;
    } else if (formData.state_id === ResourceState.INACTIVE_COMPLETED) {
      newStateId = ResourceState.COMPLETED;
    }
    
    setFormData(prev => ({
      ...prev,
      state_id: newStateId,
      new_state_id: newStateId
    }));
  };

  const openAddSectionModal = () => {
    const nextOrder = formData.sections.length > 0 
      ? Math.max(...formData.sections.map(section => section.order)) + 1 
      : 1;
    
    setSectionModal({
      isOpen: true,
      mode: 'add',
      sectionIndex: null,
      sectionName: `Section ${nextOrder}`,
      errors: {}
    });
  };

  const deleteSection = async (index: number) => {
    if (!id) return;
    
    const section = formData.sections[index];
    if (!section.id) return;
    
    // Show confirmation modal
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Section',
      message: `Are you sure you want to delete section "${section.name}"? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        await performDeleteSection(index);
      }
    });
  };

  const performDeleteSection = async (index: number) => {
    if (!id) return;
    
    const section = formData.sections[index];
    if (!section.id) return;

    try {
      const response = await postData<EvalFormDataResponse>(
        api(PATH_SEGMENTS.EVALFORM, id, PATH_SEGMENTS.SECTION, section.id),
        section,
        'DELETE'
      );
      setFormData(normalizeEvalFormData(response.evalform));
      if (activeSection === index) {
        setActiveSection(null);
      }
      showToast('✅ Section deleted successfully', 'success', 3000);
    } catch (err) {
      const newErrors = await handleApiError(err);
      setFieldErrors(prevErrors => ({
        ...prevErrors,
        ...newErrors
      }));
      if (Object.keys(newErrors).length === 0) {
        showToast('❌ Failed to delete section', 'error', 3000);
      }
    }
  };




  const openAddQuestionModal = (sectionIndex: number) => {
    const section = formData.sections[sectionIndex];
    if (!section.id) return;
    
    // Calculate next order
    const nextOrder = section.questions.length > 0 
      ? Math.max(...section.questions.map(q => q.order)) + 1 
      : 1;
        
    const newQuestion: EFQuestionData = {
      order: nextOrder,
      points: 10,
      description: `New Question ${nextOrder}`,
      type_id: QuestionType.PROMPT,
      option_type_id: OptionType.YES_NO,
      prompt: '',
      examples: [],
      options: [
        { order: 1, points: 0, description: 'Yes or True' },
        { order: 2, points: 0, description: 'No or False' }
      ],
      moments: [],
      section_id: section.id
    };

    setModalState({
      isOpen: true,
      question: newQuestion,
      sectionIndex: sectionIndex,
      questionIndex: null,
      isNew: true,
      errors: {},
      momentRowCategories: {}
    });
  };
  
  const openEditQuestionModal = (sectionIndex: number, questionIndex: number) => {
    const question = formData.sections[sectionIndex]?.questions[questionIndex];
    if (!question) return;
    
    // Create a deep copy of the question for editing
    const questionCopy: EFQuestionData = {
      ...question,
      options: question.options ? [...question.options] : [],
      moments: question.moments ? [...question.moments] : [],
      examples: question.examples ? [...question.examples] : []
    };
    
    // Initialize moment row categories
    const initialCategories: {[key: string]: number} = {};
    if (questionCopy.moments) {
      questionCopy.moments.forEach((moment, idx) => {
        const momentDetails = moments.find(m => m.id === moment.moment_id);
        if (momentDetails?.mmt_category_id) {
          initialCategories[`modal-${idx}`] = momentDetails.mmt_category_id;
        }
      });
    }
    
    setModalState({
      isOpen: true,
      question: questionCopy,
      sectionIndex: sectionIndex,
      questionIndex: questionIndex,
      isNew: false,
      errors: {},
      momentRowCategories: initialCategories
    });
  };

  const openDuplicateQuestionModal = (sectionIndex: number, questionIndex: number) => {
    const questionToDuplicate = formData.sections[sectionIndex]?.questions[questionIndex];
    if (!questionToDuplicate) return;
    
    const section = formData.sections[sectionIndex];
    if (!section.id) return;
    
    // Calculate next order
    const nextOrder = section.questions.length > 0 
      ? Math.max(...section.questions.map(q => q.order)) + 1 
      : 1;
    
    // Create a deep copy of the question for duplication (remove id to make it a new question)
    const duplicatedQuestion: EFQuestionData = {
      ...questionToDuplicate,
      id: undefined, // Remove id so it's treated as a new question
      description: `${questionToDuplicate.description} (Copy)`,
      order: nextOrder,
      options: questionToDuplicate.options ? questionToDuplicate.options.map(opt => ({ ...opt })) : [],
      moments: questionToDuplicate.moments ? questionToDuplicate.moments.map(mom => ({ ...mom })) : [],
      examples: questionToDuplicate.examples ? [...questionToDuplicate.examples] : [],
      section_id: section.id
    };
    
    // Initialize moment row categories
    const initialCategories: {[key: string]: number} = {};
    if (duplicatedQuestion.moments) {
      duplicatedQuestion.moments.forEach((moment, idx) => {
        const momentDetails = moments.find(m => m.id === moment.moment_id);
        if (momentDetails?.mmt_category_id) {
          initialCategories[`modal-${idx}`] = momentDetails.mmt_category_id;
        }
      });
    }
    
    setModalState({
      isOpen: true,
      question: duplicatedQuestion,
      sectionIndex: sectionIndex,
      questionIndex: null, // null because it's a new question
      isNew: true,
      errors: {},
      momentRowCategories: initialCategories
    });
  };
  
  const closeQuestionModal = () => {
    setModalState({
      isOpen: false,
      question: null,
      sectionIndex: null,
      questionIndex: null,
      isNew: false,
      errors: {},
      momentRowCategories: {}
    });
    questionModalSaveState.reset();
  };

  // Modal helper functions for moments
  const addMomentToModal = (momentIndex: number | null = null) => {
    if (!modalState.question) return;
    
    const currentMoments = modalState.question.moments || [];
    let insertPosition = currentMoments.length;
    if (momentIndex !== null && momentIndex >= 0 && momentIndex < currentMoments.length) {
      insertPosition = momentIndex + 1;
    }
    
    const newMoment: EFQuestionMomentData = {
      order: insertPosition + 1,
      moment_id: 0,
      operation_id: MomentOperation.NONE
    };
    
    const updatedMoments = [...currentMoments];
    updatedMoments.splice(insertPosition, 0, newMoment);
    
    // Recalculate order
    updatedMoments.forEach((moment, idx) => {
      moment.order = idx + 1;
    });
    
    updateModalState({
      question: {
        ...modalState.question,
        moments: updatedMoments
      }
    });
  };

  const removeMomentFromModal = (momentIndex: number) => {
    if (!modalState.question || !modalState.question.moments) return;
    
    // Don't remove if this is the last moment
    if (modalState.question.moments.length <= 1) return;
    
    const updatedMoments = [...modalState.question.moments];
    updatedMoments.splice(momentIndex, 1);
    
    // Recalculate order
    updatedMoments.forEach((moment, idx) => {
      moment.order = idx + 1;
    });
    
    updateModalState({
      question: {
        ...modalState.question,
        moments: updatedMoments
      }
    });
  };

  const updateModalMomentCategory = (momentIndex: number, categoryId: number) => {
    if (!modalState.question) return;
    
    const rowKey = `modal-${momentIndex}`;
    updateModalState({
      momentRowCategories: {
        ...modalState.momentRowCategories,
        [rowKey]: categoryId
      }
    });
    
    // Reset moment selection when category changes
    if (categoryId > 0 && modalState.question.moments && modalState.question.moments[momentIndex]) {
      const updatedMoments = [...modalState.question.moments];
      updatedMoments[momentIndex] = {
        ...updatedMoments[momentIndex],
        moment_id: 0
      };
      updateModalState({
        question: {
          ...modalState.question,
          moments: updatedMoments
        }
      });
    }
  };

  const updateModalMomentId = (momentIndex: number, momentId: number) => {
    if (!modalState.question || !modalState.question.moments) return;
    
    const updatedMoments = [...modalState.question.moments];
    updatedMoments[momentIndex] = {
      ...updatedMoments[momentIndex],
      moment_id: momentId
    };
    
    updateModalState({
      question: {
        ...modalState.question,
        moments: updatedMoments
      }
    });
  };

  const updateModalMomentOperation = (momentIndex: number, operationId: number) => {
    if (!modalState.question || !modalState.question.moments) return;
    
    const updatedMoments = [...modalState.question.moments];
    updatedMoments[momentIndex] = {
      ...updatedMoments[momentIndex],
      operation_id: operationId
    };
    
    updateModalState({
      question: {
        ...modalState.question,
        moments: updatedMoments
      }
    });
  };

  // Modal helper functions for options
  const addOptionToModal = (optionIndex: number | null = null) => {
    if (!modalState.question) return;
    
    // Prevent adding options to YES_NO questions
    if (Number(modalState.question.option_type_id) === OptionType.YES_NO) {
      return;
    }
    
    const currentOptions = modalState.question.options || [];
    let insertPosition = currentOptions.length;
    if (optionIndex !== null && optionIndex >= 0 && optionIndex < currentOptions.length) {
      insertPosition = optionIndex + 1;
    }
    
    const nextOrder = currentOptions.length > 0 
      ? Math.max(...currentOptions.map(opt => opt.order)) + 1 
      : 1;
    
    const newOption = {
      order: nextOrder,
      points: 0,
      description: `Option ${nextOrder}`
    };
    
    const updatedOptions = [...currentOptions];
    updatedOptions.splice(insertPosition, 0, newOption);
    
    // Recalculate order
    updatedOptions.forEach((option, idx) => {
      option.order = idx + 1;
    });
    
    updateModalState({
      question: {
        ...modalState.question,
        options: updatedOptions
      }
    });
  };

  const removeOptionFromModal = (optionIndex: number) => {
    if (!modalState.question || !modalState.question.options) return;
    
    // Prevent deleting options from YES_NO questions
    if (Number(modalState.question.option_type_id) === OptionType.YES_NO) {
      return;
    }

    // Don't remove if this is the last option
    if (modalState.question.options.length <= 1) return;
    
    const updatedOptions = [...modalState.question.options];
    updatedOptions.splice(optionIndex, 1);
    
    // Recalculate order
    updatedOptions.forEach((option, idx) => {
      option.order = idx + 1;
    });
    
    updateModalState({
      question: {
        ...modalState.question,
        options: updatedOptions
      }
    });
  };

  const updateModalOptionDescription = (optionIndex: number, description: string) => {
    if (!modalState.question || !modalState.question.options) return;
    
    const updatedOptions = [...modalState.question.options];
    updatedOptions[optionIndex] = {
      ...updatedOptions[optionIndex],
      description
    };
    
    updateModalState({
      question: {
        ...modalState.question,
        options: updatedOptions
      }
    });
  };

  const updateModalOptionPoints = (optionIndex: number, points: number) => {
    if (!modalState.question || !modalState.question.options) return;
    
    // Allow updating points for all options, including No option for Yes/No questions
    const updatedOptions = [...modalState.question.options];
    updatedOptions[optionIndex] = {
      ...updatedOptions[optionIndex],
      points
    };
    
    updateModalState({
      question: {
        ...modalState.question,
        options: updatedOptions
      }
    });
  };
  // Update deleteQuestion to handle new questions
  const deleteQuestion = async (sectionIndex: number, questionIndex: number) => {
    if (!id) return;

    const question = formData.sections[sectionIndex].questions[questionIndex];
    if (!question.id) return;

    // Show confirmation modal
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Question',
      message: `Are you sure you want to delete question "${question.description}"? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        await performDeleteQuestion(sectionIndex, questionIndex);
      }
    });
  };

  const performDeleteQuestion = async (sectionIndex: number, questionIndex: number) => {
    if (!id) return;

    const question = formData.sections[sectionIndex].questions[questionIndex];
    if (!question.id) return;

    try {
      const response = await postData<EvalFormDataResponse>(
        api(PATH_SEGMENTS.EVALFORM, id, PATH_SEGMENTS.QUESTION, question.id),
        question,
        'DELETE'
      );
      const normalizedData = normalizeEvalFormData(response.evalform);
      // Update section points after deletion
      const updatedData = updateQuestionAndSectionPoints(sectionIndex, undefined, normalizedData) || normalizedData;
      setFormData(updatedData);
      showToast('✅ Question deleted successfully', 'success', 3000);
    } catch (err) {
      const newErrors = await handleApiError(err);
      setFieldErrors(prevErrors => ({
        ...prevErrors,
        ...newErrors
      }));
      if (Object.keys(newErrors).length === 0) {
        showToast('❌ Failed to delete question', 'error', 3000);
      }
    }
  };


  const calculateTotalPoints = () => {
    let total = 0;
    formData.sections.forEach(section => {
      section.questions.forEach(question => {
        total += question.points;
      });
    });
    return total;
  };

  // Update question and section points in formData
  const updateQuestionAndSectionPoints = (sectionIndex: number, questionIndex?: number, dataToUpdate?: EvalFormData) => {
    const data = dataToUpdate || formData;
    const updatedSections = [...data.sections];
    const section = updatedSections[sectionIndex];
    
    // If questionIndex is provided, update that specific question's points
    if (questionIndex !== undefined) {
      const question = section.questions[questionIndex];
      question.points = calculateQuestionPoints(question);
    } else {
      // Otherwise, update all questions in the section
      section.questions.forEach(question => {
        question.points = calculateQuestionPoints(question);
      });
    }
    
    // Update section points (sum of all question points)
    section.points = section.questions.reduce((sum, question) => sum + (question.points || 0), 0);
    
    if (dataToUpdate) {
      // If dataToUpdate is provided, return the updated data
      return {
        ...dataToUpdate,
        sections: updatedSections
      };
    } else {
      // Otherwise, update state directly
      setFormData(prev => ({
        ...prev,
        sections: updatedSections
      }));
    }
  };


  const updateOptionPoints = (sectionIndex: number, questionIndex: number, optionIndex: number, points: number) => {
    const updatedSections = [...formData.sections];
    const question = updatedSections[sectionIndex].questions[questionIndex];
    
    // Update the option points - allow updating points for all options including No option
    if (!question.options) {
      question.options = [];
    }
    question.options[optionIndex].points = points;
    console.log('updateOptionPoints: option points = ', question.options[optionIndex].points);

    // Update the form data with the new question
    updatedSections[sectionIndex].questions[questionIndex] = question;

    setFormData(prev => ({
      ...prev,
      sections: updatedSections
    }));
    
    // Update question and section points in formData
    updateQuestionAndSectionPoints(sectionIndex, questionIndex);
  };

  const saveForm = async () => {
    try {
      saveButtonState.startLoading();
      setFieldErrors({}); // Clear any existing errors
      
      // Validate form before saving
      const validationResult = validateForm();
      if (!validationResult) {
        console.log('saveForm: form validation failed');
        
        // Set flag to scroll to errors when they're updated
        // Use setTimeout to allow React state to update
        setTimeout(() => {
          const hasFieldErrors = Object.keys(fieldErrors).length > 0;
          if (hasFieldErrors) {
            setShouldScrollToErrors(true);
          } else {
            // No field errors but validation failed - show generic error toast
            showToast('❌ Save failed, try again', 'error', 3000);
          }
        }, 100);
        
        saveButtonState.reset();
        return;
      }

      const updatedFormData = {
        ...formData,
        name: editedName || formData.name,
        points: calculateTotalPoints(),
        state_id: formData.state_id || ResourceState.NEW,
        new_state_id: ResourceState.IN_PROGRESS,
        is_shared_id: formData.is_shared_id || 1,
        sections: formData.sections || []
      };
      
      console.log('Saving form data:', JSON.stringify(updatedFormData, null, 2));
      
      let response: EFErrorResponse;
      if (id) {
        // Update existing form
        response = await postData<EFErrorResponse>(api(PATH_SEGMENTS.EVALFORM, id), updatedFormData, 'PUT');
      } else {
        // Create new form
        response = await postData<EFErrorResponse>(api(PATH_SEGMENTS.EVALFORM), updatedFormData);
      }
      
      // Show success toast
      showToast('✅ Evaluation Form ' + response.evalform.id + ' saved successfully', 'success', 3000);
      saveButtonState.showSuccess(2000);
      
      // Navigate after brief delay
      setTimeout(() => {
        if (response.evalform.id) {
          navigate(route(PATH_SEGMENTS.EVALFORM, response.evalform.id));
        }
      }, 500);
    } catch (err) {
      await handleApiError(err);
      
      // Check if there are field errors after API error handling
      const hasFieldErrors = Object.keys(fieldErrors).length > 0;
      
      if (hasFieldErrors) {
        // Scroll to first error field
        setTimeout(() => {
          scrollToFirstError(fieldErrors);
        }, 100);
      } else {
        // No field errors but save failed - show generic error toast
        showToast('❌ Save failed, try again', 'error', 3000);
      }
      
      saveButtonState.reset();
    }
  };

  // Track if we should scroll to errors after validation fails
  const [shouldScrollToErrors, setShouldScrollToErrors] = useState(false);

  // Scroll to first error when fieldErrors change after validation failure
  useEffect(() => {
    if (shouldScrollToErrors && Object.keys(fieldErrors).length > 0) {
      setTimeout(() => {
        scrollToFirstError(fieldErrors);
        setShouldScrollToErrors(false);
      }, 100);
    }
  }, [fieldErrors, shouldScrollToErrors]);

  const submitForm = async () => {
    try {
      submitButtonState.startLoading();
      
      const updatedFormData = {
        ...formData,
        points: calculateTotalPoints(),
        new_state_id: ResourceState.COMPLETED,
        is_shared_id: formData.is_shared_id || 1,
      };

      console.log('Submitting form data:', JSON.stringify(updatedFormData, null, 2));
      
      if (id) {
        await postData(api(PATH_SEGMENTS.EVALFORM, id), updatedFormData, 'PUT');
        
        // Show success toast
        showToast('✅ Evaluation Form ' + id + ' saved successfully', 'success', 3000);
        submitButtonState.showSuccess(2000);
      } else {
        const response = await postData<{id: number}>(api(PATH_SEGMENTS.EVALFORM), updatedFormData);
        
        // Show success toast
        showToast('✅ Evaluation Form ' + response.id + ' saved successfully', 'success', 3000);
        submitButtonState.showSuccess(2000);
        
        // Navigate after brief delay
        setTimeout(() => {
          navigate(route(PATH_SEGMENTS.EVALFORM, response.id));
        }, 500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while submitting');
      
      // Check if there are field errors
      const hasFieldErrors = Object.keys(fieldErrors).length > 0;
      
      if (hasFieldErrors) {
        // Scroll to first error field
        setTimeout(() => {
          scrollToFirstError(fieldErrors);
        }, 100);
      } else {
        // No field errors but submit failed - show generic error toast
        showToast('❌ Submit failed, try again', 'error', 3000);
      }
      
      submitButtonState.reset();
    }
  };

  const deleteForm = async () => {
    if (!id) return;
    
    try {
      const updatedFormData = {
        ...formData,
        state_id: ResourceState.DELETED
      };
      
      await postData(api(PATH_SEGMENTS.EVALFORM, id), updatedFormData, 'PUT');
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while deleting');
    }
  };


  

  const addOption = (sectionIndex: number, questionIndex: number, insertAfterIndex: number | null = null) => {
    // Create a deep copy of sections
    const updatedSections = JSON.parse(JSON.stringify(formData.sections));
    
    // Get the options for the current question
    const question = updatedSections[sectionIndex].questions[questionIndex];
    const options = question.options || [];
    
    // Prevent adding options to YES_NO questions as they should only have exactly 2 fixed options
    if (Number(question.option_type_id) === OptionType.YES_NO) {
      return;
    }
    
    // Determine the next available order
    const nextOrder = options.length > 0 
      ? Math.max(...options.map((option: any) => option.order)) + 1 
      : 1;
    
    // Create new option with default values
    const newOption = {
      order: nextOrder,
      points: 0,
      description: `Option ${nextOrder}`,
    };
    
    // Determine the insertion position
    let insertPosition = options.length;
    if (insertAfterIndex !== null && insertAfterIndex >= 0 && insertAfterIndex < options.length) {
      insertPosition = insertAfterIndex + 1;
    }
    
    // Add the new option to the question
    if (!question.options) {
      question.options = [newOption];
    } else {
      // Insert at the specific position
      question.options.splice(insertPosition, 0, newOption);
    }
    
    // Recalculate order for all options
    question.options.forEach((option: any, idx: number) => {
      option.order = idx + 1;
    });
    
    // Update the form data
    setFormData(prev => ({
      ...prev,
      sections: updatedSections
    }));
  };
  
  const deleteOption = (sectionIndex: number, questionIndex: number, optionIndex: number) => {
    const updatedSections = JSON.parse(JSON.stringify(formData.sections));
    const question = updatedSections[sectionIndex].questions[questionIndex];
    
    if (!question.options) return;
    
    // Prevent deleting options from YES_NO questions as they should always have exactly 2 options
    if (Number(question.option_type_id) === OptionType.YES_NO) {
      return;
    }
    
    // Don't remove if this is the last option
    if (question.options.length <= 1) {
      return;
    }
    
    // Remove the option
    question.options.splice(optionIndex, 1);
    
    // Recalculate order for all remaining options
    question.options.forEach((option: any, idx: number) => {
      option.order = idx + 1;
    });
    
    setFormData(prev => ({
      ...prev,
      sections: updatedSections
    }));
  };
  
  const updateOptionDescription = (sectionIndex: number, questionIndex: number, optionIndex: number, description: string) => {
    const section = formData.sections[sectionIndex];
    const question = section.questions[questionIndex];
    if (!question.options) return;
    const option = question.options[optionIndex];
    if (!option) return;
    clearFieldError(`section-${section.order}-question-${question.order}-option-${option.order}-description`);
    const updatedSections = JSON.parse(JSON.stringify(formData.sections));
    updatedSections[sectionIndex].questions[questionIndex].options[optionIndex].description = description;
    updatedSections[sectionIndex].questions[questionIndex].options.sort((a: any, b: any) => a.order - b.order);
    setFormData(prev => ({ ...prev, sections: updatedSections }));
  };


  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedName(e.target.value);
    // Clear error when user types
    if (getFieldError('name')) {
      clearFieldError('name');
    }
  };

  const handleNameSave = async () => {
    if (!id) return;
    
    const formName = editedName.trim();
    
    // Validate form name
    if (!formName) {
      setFieldErrors(prevErrors => ({
        ...prevErrors,
        name: 'Form name is required'
      }));
      formNameSaveState.reset();
      return;
    }
    
    formNameSaveState.startLoading();
    clearFieldError('name');
    
    try {
      const updatedFormData = {
        ...formData,
        name: formName,
        new_state_id: formData.new_state_id || ResourceState.IN_PROGRESS
      };
      console.log('handleNameSave: formData = ', formData);
      console.log('handleNameSave: updatedFormData = ', updatedFormData);
      const response = await postData<EvalFormDataResponse>(
        api(PATH_SEGMENTS.EVALFORM, id),
        updatedFormData,
        'PUT'
      );
      
      setFormData(normalizeEvalFormData(response.evalform));
      setIsEditingName(false);
      setOriginalFormName('');
      formNameSaveState.showSuccess(2000);
      showToast('✅ Form name saved successfully', 'success', 3000);
    } catch (err) {
      const newErrors = await handleApiError(err);
      setFieldErrors(prevErrors => ({
        ...prevErrors,
        ...newErrors
      }));
      formNameSaveState.reset();
      if (Object.keys(newErrors).length === 0) {
        showToast('❌ Failed to save form name', 'error', 3000);
      }
    }
  };

  const handleNameCancel = () => {
    setEditedName(originalFormName || formData.name);
    setIsEditingName(false);
    setOriginalFormName('');
    clearFieldError('name');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNameSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleNameCancel();
    }
  };
  
  const startEditingName = () => {
    setOriginalFormName(formData.name);
    setEditedName(formData.name);
    setIsEditingName(true);
  };


  const scrollToElement = (element: HTMLElement | null) => {
    if (element && formEditorRef.current) {
      const editorRect = formEditorRef.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = elementRect.top - editorRect.top + formEditorRef.current.scrollTop;
      
      formEditorRef.current.scrollTo({
        top: scrollTop - 55, // Add some padding
        behavior: 'smooth'
      });
    }
  };

  const handlePreviewSectionClick = (sectionIndex: number) => {
    const sectionElement = sectionRefs.current[sectionIndex];
    scrollToElement(sectionElement);
  };

  const handlePreviewQuestionClick = (sectionIndex: number, questionIndex: number) => {
    const questionElement = questionRefs.current[`${sectionIndex}-${questionIndex}`];
    scrollToElement(questionElement);
  };


  const toggleSection = (sectionIndex: number) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex]
    }));
  };

  const openDuplicateSectionModal = (sectionIndex: number) => {
    const sectionToDuplicate = formData.sections[sectionIndex];
    setSectionModal({
      isOpen: true,
      mode: 'duplicate',
      sectionIndex: sectionIndex,
      sectionName: `${sectionToDuplicate.name} (Copy)`,
      errors: {}
    });
  };

  const closeSectionModal = () => {
    setSectionModal({
      isOpen: false,
      mode: 'add',
      sectionIndex: null,
      sectionName: '',
      errors: {}
    });
    sectionModalSaveState.reset();
  };

  // Validate section name - returns error message or null
  const validateSectionName = (sectionName: string, excludeIndex?: number): string | null => {
    const trimmedName = sectionName.trim();
    
    // Check if name is empty
    if (!trimmedName) {
      return 'Section name is required';
    }
    
    // Check for duplicate section names
    // If excludeIndex is provided, exclude that section from the check (for editing)
    const existingSectionNames = formData.sections
      .filter((_, idx) => excludeIndex === undefined || idx !== excludeIndex)
      .map(s => s.name.trim().toLowerCase());
    
    if (existingSectionNames.includes(trimmedName.toLowerCase())) {
      return 'Section name must be unique';
    }
    
    return null;
  };

  const handleSectionModalNameChange = (value: string) => {
    setSectionModal(prev => ({
      ...prev,
      sectionName: value,
      errors: {} // Clear errors when user types
    }));
  };

  const saveSection = async () => {
    if (!id) return;
    
    const sectionName = sectionModal.sectionName.trim();
    
    // Validate section name
    const validationError = validateSectionName(sectionName);
    if (validationError) {
      setSectionModal(prev => ({
        ...prev,
        errors: { name: validationError }
      }));
      return;
    }
    
    sectionModalSaveState.startLoading();
    setSectionModal(prev => ({ ...prev, errors: {} }));
    
    try {
      const nextOrder = formData.sections.length > 0 
        ? Math.max(...formData.sections.map(section => section.order)) + 1 
        : 1;
      
      let newSection: EFSectionData;
      
      if (sectionModal.mode === 'duplicate' && sectionModal.sectionIndex !== null) {
        // Duplicate mode: copy questions from existing section
        const sectionToDuplicate = formData.sections[sectionModal.sectionIndex];
        newSection = {
          name: sectionName,
          description: sectionName,
          order: nextOrder,
          points: 0,
          form_id: Number(id),
          questions: sectionToDuplicate.questions.map(question => ({
            ...question,
            id: undefined, // Remove id to create new questions
            order: question.order,
            section_id: undefined, // Will be set by backend
            options: question.options ? question.options.map(opt => ({ ...opt })) : [],
            moments: question.moments ? question.moments.map(mom => ({ ...mom })) : [],
            examples: question.examples ? [...question.examples] : []
          }))
        };
      } else {
        // Add mode: create empty section
        newSection = {
          name: sectionName,
          description: sectionName,
          order: nextOrder,
          points: 0,
          form_id: Number(id),
          questions: []
        };
      }
      
      const response = await postData<EvalFormDataResponse>(
        api(PATH_SEGMENTS.EVALFORM, id, PATH_SEGMENTS.SECTION),
        newSection,
        'PUT'
      );
      
      setFormData(normalizeEvalFormData(response.evalform));
      sectionModalSaveState.showSuccess(2000);
      
      const successMessage = sectionModal.mode === 'duplicate' 
        ? '✅ Section duplicated successfully' 
        : '✅ Section created successfully';
      showToast(successMessage, 'success', 3000);
      
      // Set the new section as active (for add mode)
      if (sectionModal.mode === 'add') {
        const newSectionIndex = response.evalform.sections.findIndex(s => s.order === nextOrder);
        setActiveSection(newSectionIndex);
      }
      
      // Close modal after brief delay
      setTimeout(() => {
        closeSectionModal();
      }, 500);
    } catch (err) {
      const newErrors = await handleApiError(err);
      // Map API errors to modal error format
      const modalErrors: {[key: string]: string} = {};
      Object.entries(newErrors).forEach(([field, message]) => {
        // Map common field names to modal error keys
        // Handle both "name" and nested paths like "sections.0.name"
        if (field === 'name' || field.endsWith('.name') || field.includes('name')) {
          modalErrors['name'] = message;
        } else {
          // For other errors, use the field name as-is
          modalErrors[field] = message;
        }
      });
      
      setSectionModal(prev => ({
        ...prev,
        errors: {
          ...prev.errors,
          ...modalErrors
        }
      }));
      sectionModalSaveState.reset();
      if (Object.keys(modalErrors).length === 0) {
        const errorMessage = sectionModal.mode === 'duplicate'
          ? '❌ Failed to duplicate section'
          : '❌ Failed to create section';
        showToast(errorMessage, 'error', 3000);
      }
    }
  };

  const duplicateQuestion = (sectionIndex: number, questionIndex: number) => {
    const questionToDuplicate = formData.sections[sectionIndex].questions[questionIndex];
    const newQuestion: EFQuestionData = {
      ...questionToDuplicate,
      description: `${questionToDuplicate.description} (Copy)`,
      order: questionToDuplicate.order + 1, // Set order to be after the original question
      options: questionToDuplicate.options ? [...questionToDuplicate.options] : [],
      moments: questionToDuplicate.moments ? [...questionToDuplicate.moments] : []
    };

    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].questions.splice(questionIndex + 1, 0, newQuestion);
    
    // Update order for all questions after the inserted question
    updatedSections[sectionIndex].questions.forEach((question, idx) => {
      question.order = idx + 1;
    });
    
    setFormData(prev => ({
      ...prev,
      sections: updatedSections
    }));
  };

  const isFormEditable = () => {
    return formData.state_id === ResourceState.NEW || formData.state_id === ResourceState.IN_PROGRESS;
  };

  const handleMomentClick = (momentId: number) => {
    if (momentId) {
      navigate(route(PATH_SEGMENTS.MOMENT, momentId));
    }
  };

  // Add these helper functions for API calls
  const updateSection = async (section: EFSectionData) => {
    if (!id || !section.id) return;
    try {
      const response = await postData<EvalFormDataResponse>(
        api(PATH_SEGMENTS.EVALFORM, id, PATH_SEGMENTS.SECTION, section.id),
        section,
        'PUT'
      );
      setFormData(normalizeEvalFormData(response.evalform));
    } catch (err) {
      await handleApiError(err);
    }
  };

  const updateQuestion = async (question: EFQuestionData) => {
    if (!id || !question.id) return;
    console.log('updateQuestion: question = ', question);
    try {
      const response = await postData<EvalFormDataResponse>(
        api(PATH_SEGMENTS.EVALFORM, id, PATH_SEGMENTS.QUESTION, question.id),
        question,
        'PUT'
      );
      setFormData(normalizeEvalFormData(response.evalform));
      console.log('updateQuestion: response.evalform = ', response.evalform);
    } catch (err) {
      await handleApiError(err);
    }
  };

  const handleSectionNameChange = (index: number, value: string) => {
    const section = formData.sections[index];
    // Clear any existing field error
    clearFieldError(`section-${section.order}-name`);
    
    setEditingSectionName(prev => prev && prev.index === index 
      ? { index, value, original: prev.original }
      : { index, value, original: section.name });
    // Update local state immediately for responsive UI
    const updatedSections = [...formData.sections];
    updatedSections[index] = {
      ...updatedSections[index],
      name: value
    };
    setFormData(prev => ({
      ...prev,
      sections: updatedSections
    }));
  };

  const handleSectionNameSave = async (index: number) => {
    if (!editingSectionName || editingSectionName.index !== index || !id) return;
    
    const section = formData.sections[index];
    if (!section.id) return;
    
    const sectionName = editingSectionName.value.trim();
    
    const validationError = validateSectionName(sectionName, index);
    if (validationError) {
      setFieldErrors(prevErrors => ({
        ...prevErrors,
        [`section-${section.order}-name`]: validationError
      }));
      updateSectionSaveState(index, {});
      return;
    }
    
    updateSectionSaveState(index, { isLoading: true });
    clearFieldError(`section-${section.order}-name`);
    
    const updatedSection = {
      ...section,
      name: sectionName
    };
    
    try {
      const response = await postData<EvalFormDataResponse>(
        api(PATH_SEGMENTS.EVALFORM, id, PATH_SEGMENTS.SECTION, section.id),
        updatedSection,
        'PUT'
      );
      setFormData(normalizeEvalFormData(response.evalform));
      setEditingSectionName(null);
      updateSectionSaveState(index, { isSuccess: true });
      setTimeout(() => updateSectionSaveState(index, {}), 2000);
      showToast('✅ Section name saved successfully', 'success', 3000);
    } catch (err) {
      const newErrors = await handleApiError(err);
      setFieldErrors(prevErrors => ({
        ...prevErrors,
        ...newErrors
      }));
      updateSectionSaveState(index, {});
      if (Object.keys(newErrors).length === 0) {
        showToast('❌ Failed to save section name', 'error', 3000);
      }
    }
  };

  const handleSectionNameCancel = (index: number) => {
    const section = formData.sections[index];
    const originalName = editingSectionName?.original || section.name;
    handleSectionNameChange(index, originalName);
    setEditingSectionName(null);
    clearFieldError(`section-${section.order}-name`);
  };

  const handleSectionNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSectionNameSave(index);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleSectionNameCancel(index);
    }
  };

  // Question description editing is now done in the modal only

  // Question description editing is now done in the modal only
  
  // Validate question in modal
  const validateQuestion = (question: EFQuestionData): {[key: string]: string} => {
    const errors: {[key: string]: string} = {};
    
    if (!question.description?.trim()) {
      errors['description'] = 'Question description is required';
    }
    
    if (question.type_id === QuestionType.PROMPT && !question.prompt?.trim()) {
      errors['prompt'] = 'Prompt is required for prompt type questions';
    }
    
    if (question.type_id === QuestionType.MOMENT) {
      if (!question.moments || question.moments.length === 0) {
        errors['moments'] = 'At least one moment is required for moment type questions';
      } else {
        question.moments.forEach((moment, idx) => {
          if (!moment.moment_id) {
            errors[`moment-${idx}`] = 'Moment is required';
          }
          const isLastMoment = moment.order === Math.max(...(question.moments || []).map(m => m.order));
          if (isLastMoment && moment.operation_id !== MomentOperation.NONE) {
            errors[`moment-${idx}-operation`] = 'Select NONE for the last moment';
          } else if (!isLastMoment && moment.operation_id === MomentOperation.NONE) {
            errors[`moment-${idx}-operation`] = 'Select an operation';
          }
        });
      }
    }
    
    if (question.options && question.options.length > 0) {
      question.options.forEach((option, idx) => {
        if (!option.description?.trim()) {
          errors[`option-${idx}-description`] = 'Option description is required';
        }
      });
    }
    
    return errors;
  };
  
  // Save question from modal
  const saveQuestionFromModal = async () => {
    if (!id || !modalState.question || modalState.sectionIndex === null) return;
    
    // Validate
    const validationErrors = validateQuestion(modalState.question);
    if (Object.keys(validationErrors).length > 0) {
      updateModalState({ errors: validationErrors });
      return;
    }
    
    questionModalSaveState.startLoading();
    updateModalState({ errors: {} });
    
    try {
      let response: EvalFormDataResponse;
      
      if (modalState.isNew) {
        // Create new question
        response = await postData<EvalFormDataResponse>(
          api(PATH_SEGMENTS.EVALFORM, id, PATH_SEGMENTS.QUESTION),
          modalState.question,
          'PUT'
        );
      } else if (modalState.question.id) {
        // Update existing question
        response = await postData<EvalFormDataResponse>(
          api(PATH_SEGMENTS.EVALFORM, id, PATH_SEGMENTS.QUESTION, modalState.question.id),
          modalState.question,
          'PUT'
        );
      } else {
        throw new Error('Invalid question state');
      }
      
      const normalizedData = normalizeEvalFormData(response.evalform);
      
      // Update question and section points in formData after save
      let finalData = normalizedData;
      if (modalState.sectionIndex !== null) {
        const savedSection = normalizedData.sections[modalState.sectionIndex];
        const savedQuestionIndex = savedSection.questions.findIndex(
          q => q.id === modalState.question?.id || 
          (modalState.isNew && q.description === modalState.question?.description && q.order === modalState.question?.order)
        );
        if (savedQuestionIndex !== -1) {
          finalData = updateQuestionAndSectionPoints(modalState.sectionIndex, savedQuestionIndex, normalizedData) || normalizedData;
        }
      }
      
      setFormData(finalData);
      
      questionModalSaveState.showSuccess(2000);
      showToast('✅ Question saved successfully', 'success', 3000);
      
      // Close modal after brief delay
      setTimeout(() => {
        closeQuestionModal();
      }, 500);
    } catch (err) {
      const newErrors = await handleApiError(err);
      updateModalState({
        errors: {
          ...modalState.errors,
          ...newErrors
        }
      });
      questionModalSaveState.reset();
      if (Object.keys(newErrors).length === 0) {
        showToast('❌ Failed to save question', 'error', 3000);
      }
    }
  };

  // Question description editing is now done in the modal only

  // Update handleApiError to use state setters directly
  const handleApiError = async (err: unknown): Promise<{[key: string]: string}> => {
    const extracted = await extractApiError(err);
    const flattenedFieldErrors: {[key: string]: string} = {};

    Object.entries(extracted.fieldErrors).forEach(([field, messages]) => {
      if (Array.isArray(messages) && messages.length > 0) {
        flattenedFieldErrors[field] = messages.join(', ');
      }
    });

    if (Object.keys(flattenedFieldErrors).length > 0) {
      setError('');
      return flattenedFieldErrors;
    }

    setError(extracted.message || 'An error occurred while saving');
          return {};
  };


  if (loading) return <div className="loading-container">Loading...</div>;
  if (error) return <div className="error-container">Error: {error}</div>;

  return (
    <div className="evaluation-form">
      <main className="form-main-content">
        <div className="form-editor" ref={formEditorRef}>
          <div className="form-editor-header">
            <div className="left-content">
              {isEditingName && isFormEditable() ? (
                <div className="form-name-input-container">
                  <input
                    type="text"
                    value={editedName}
                    onChange={handleNameChange}
                    onKeyDown={handleNameKeyDown}
                    className={`form-name-input ${getFieldError('name') ? 'error' : ''} ${editedName !== originalFormName ? 'with-actions' : ''}`}
                    autoFocus
                  />
                  {editedName !== originalFormName && (
                    <div className="save-cancel-buttons-inline">
                      <button
                        type="button"
                        className="save-button-icon"
                        onClick={handleNameSave}
                        disabled={formNameSaveState.isLoading}
                        title="Save (Enter)"
                      >
                        <i className="fa fa-check"></i>
                      </button>
                      <button
                        type="button"
                        className="cancel-button-icon"
                        onClick={handleNameCancel}
                        disabled={formNameSaveState.isLoading}
                        title="Cancel (Escape)"
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  )}
                  {getFieldError('name') && (
                    <div className="error-message">{getFieldError('name')}</div>
                  )}
                </div>
              ) : (
                <h2 
                  className={`editable-form-name ${getFieldError('name') ? 'error' : ''} ${isFormEditable() ? 'editable' : ''}`}
                  onClick={() => isFormEditable() && startEditingName()}
                >
                  {formData.name}
                  {getFieldError('name') && (
                    <div className="error-message">{getFieldError('name')}</div>
                  )}
                </h2>
              )}
            </div>
            <div className="right-content"></div>
            <div className="status">
              <span>{formData.state_id === ResourceState.INACTIVE_IN_PROGRESS ? 'Inactive' : 'Active'}</span>
              {(formData.state_id === ResourceState.INACTIVE_IN_PROGRESS || 
                formData.state_id === ResourceState.IN_PROGRESS || 
                formData.state_id === ResourceState.COMPLETED || 
                formData.state_id === ResourceState.INACTIVE_COMPLETED) && (
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={formData.state_id === ResourceState.IN_PROGRESS || formData.state_id === ResourceState.COMPLETED} 
                    onChange={toggleFormActive} 
                  />
                  <span className="slider"></span>
                </label>
              )}
            </div>
          </div>
      
          {[...formData.sections].sort((a, b) => a.order - b.order).map((section, sectionIndex) => (
            <div 
              key={sectionIndex} 
              className="section"
              ref={el => { sectionRefs.current[sectionIndex] = el; }}
            >
              <div className="section-header">
                <span className="section-number">Section {section.order}</span>
                <div className="section-name-container">
                  <input 
                    type="text" 
                    value={editingSectionName?.index === sectionIndex ? editingSectionName.value : section.name} 
                    onChange={(e) => handleSectionNameChange(sectionIndex, e.target.value)} 
                    onFocus={() => {
                      if (!editingSectionName || editingSectionName.index !== sectionIndex) {
                        setEditingSectionName({ index: sectionIndex, value: section.name, original: section.name });
                      }
                    }}
                    onKeyDown={(e) => handleSectionNameKeyDown(e, sectionIndex)}
                    placeholder="Section Name"
                    className={`${getFieldError(`section-${section.order}-name`) ? 'error' : ''} ${editingSectionName?.index === sectionIndex && editingSectionName.value !== editingSectionName.original ? 'with-actions' : ''}`}
                    disabled={!isFormEditable()}
                  />
                  {editingSectionName?.index === sectionIndex && isFormEditable() && editingSectionName.value !== editingSectionName.original && (
                    <div className="save-cancel-buttons-inline">
                      <button
                        type="button"
                        className="save-button-icon"
                        onClick={() => handleSectionNameSave(sectionIndex)}
                        disabled={sectionSaveStates[sectionIndex]?.isLoading}
                        title="Save (Enter)"
                      >
                        <i className="fa fa-check"></i>
                      </button>
                      <button
                        type="button"
                        className="cancel-button-icon"
                        onClick={() => handleSectionNameCancel(sectionIndex)}
                        disabled={sectionSaveStates[sectionIndex]?.isLoading}
                        title="Cancel (Escape)"
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  )}
                  {getFieldError(`section-${section.order}-name`) && (
                    <div className="error-message">{getFieldError(`section-${section.order}-name`)}</div>
                  )}
                </div>
                <div className="section-actions">
                  <span className="points">{section.points || 0} Points</span>
                  {isFormEditable() && (
                    <>
                      <button 
                        className="icon-button duplicate" 
                        title="Copy section"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDuplicateSectionModal(sectionIndex);
                        }}
                      >
                        <i className="fa fa-copy"></i>
                      </button>
                      <button 
                        className="icon-button delete" 
                        title="Delete section"
                        onClick={() => deleteSection(sectionIndex)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="questions-container">
                {[...section.questions].sort((a, b) => a.order - b.order).map((question, questionIndex) => (
                  <div 
                    key={questionIndex} 
                    className="question"
                    ref={el => { questionRefs.current[`${sectionIndex}-${questionIndex}`] = el; }}
                  >
                    <div className="question-header">
                      <div className="question-number">Question {question.order}</div>
                      <div className="question-type-selectors">
                        <div className="type-selector">
                          <label>Evaluate Using</label>
                          <div className="type-selector-controls">
                            {isFormEditable() ? (
                              <button
                                type="button"
                                className="modal-selection-card selected"
                                onClick={(e) => {
                                e.stopPropagation();
                                  openEditQuestionModal(sectionIndex, questionIndex);
                                }}
                              >
                                {Number(question.type_id) === QuestionType.MOMENT ? 'Moment' : 'Prompt'}
                              </button>
                            ) : (
                              <div className="modal-selection-card selected read-only">
                                {Number(question.type_id) === QuestionType.MOMENT ? 'Moment' : 'Prompt'}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="type-selector">
                          <label>Options Type</label>
                          <div className="type-selector-controls">
                            {isFormEditable() ? (
                              <button
                                type="button"
                                className={`modal-selection-card selected ${Number(question.type_id) === QuestionType.MOMENT ? 'disabled' : ''}`}
                                onClick={(e) => {
                              e.stopPropagation();
                                  if (Number(question.type_id) !== QuestionType.MOMENT) {
                                    openEditQuestionModal(sectionIndex, questionIndex);
                                  }
                                }}
                                disabled={Number(question.type_id) === QuestionType.MOMENT}
                              >
                                {Number(question.option_type_id) === OptionType.YES_NO ? 'Yes / No' :
                                 Number(question.option_type_id) === OptionType.GRADING ? 'Grading' :
                                 Number(question.option_type_id) === OptionType.MULTI_CHOICE ? 'MultiChoice' : 'Yes / No'}
                              </button>
                            ) : (
                              <div className="modal-selection-card selected read-only">
                                {Number(question.option_type_id) === OptionType.YES_NO ? 'Yes / No' :
                                 Number(question.option_type_id) === OptionType.GRADING ? 'Grading' :
                                 Number(question.option_type_id) === OptionType.MULTI_CHOICE ? 'MultiChoice' : 'Yes / No'}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="question-actions">
                        {isFormEditable() && (
                          <>
                            <button 
                              className="icon-button duplicate" 
                              title="Duplicate question"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDuplicateQuestionModal(sectionIndex, questionIndex);
                              }}
                            >
                              <i className="fa fa-copy"></i>
                            </button>
                            <button 
                              className="icon-button edit" 
                              title="Edit question"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditQuestionModal(sectionIndex, questionIndex);
                              }}
                            >
                              <i className="fa fa-pencil"></i>
                            </button>
                            <button 
                              className="icon-button delete" 
                              title="Delete question"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteQuestion(sectionIndex, questionIndex);
                              }}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="question-content-compact">
                      {/* Description and Points */}
                      <div className="question-main-info">
                        <div className="question-description-compact">
                          {question.description || <span className="text-muted">No description</span>}
                        </div>
                        <div className="question-points-badge">
                          {question.points || 0} pts
                        </div>
                      </div>

                      {/* Prompt or Moment Expression - Compact */}
                      {Number(question.type_id) === Number(QuestionType.PROMPT) && question.prompt && (
                        <div className="question-prompt-compact">
                          <span className="question-meta-label">Prompt:</span>
                          <span className="question-prompt-text">{question.prompt}</span>
                          {question.examples && question.examples.length > 0 && (
                            <span className="question-examples-indicator">
                              ({question.examples.length} example{question.examples.length !== 1 ? 's' : ''})
                            </span>
                          )}
                            </div>
                      )}

                      {Number(question.type_id) === Number(QuestionType.MOMENT) && question.moments && question.moments.length > 0 && (
                        <div className="question-moment-compact">
                          <span className="question-meta-label">Moments:</span>
                          <MomentExpression 
                            moments={question.moments.map(moment => {
                              const momentDetails = moments.find(m => m.id === moment.moment_id);
                              const operationDetails = operations.find(o => o.id === moment.operation_id);
                              console.log(momentDetails, operationDetails);
                              return {
                                id: moment.moment_id,
                                name: momentDetails?.name || 'Undefined Moment',
                                operation: operationDetails?.name || 'NONE'
                              };
                            })}
                            onMomentClick={handleMomentClick}
                            isPreview={true}
                          />
                        </div>
                      )}

                      {/* Options - Compact List */}
                      {question.options && question.options.length > 0 && (
                        <div className="question-options-compact">
                          <span className="question-meta-label">Options:</span>
                          <div className="options-compact-list">
                            {[...question.options].sort((a, b) => a.order - b.order).map((option, optionIdx) => (
                              <span key={optionIdx} className="option-compact-item">
                                {option.description} <span className="option-points-compact">({option.points})</span>
                                {optionIdx < (question.options?.length || 0) - 1 && <span className="option-separator">•</span>}
                              </span>
                            ))}
                              </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                              {isFormEditable() && (
                                <button 
                    className="add-question-button"
                    onClick={() => openAddQuestionModal(sectionIndex)}
                  >
                    + Add Question
                                </button>
                              )}
                            </div>
            </div>
          ))}
          {isFormEditable() && (
            <button 
              type="button"
              className="add-section-button"
              onClick={openAddSectionModal}
            >
              + Add Section
            </button>
          )}
                          </div>

        <div className="form-preview">
          <FormPreviewPanel
            formData={formData}
            moments={moments}
            operations={operations}
          />
                            </div>
      </main>

      <footer className="form-footer">
      {isFormEditable() && (
          <>
            <button className="delete-button" onClick={deleteForm}>Delete</button>
            <div className="right-buttons">
            <button 
              className="save-button" 
              onClick={saveForm}
              disabled={saveButtonState.isLoading || submitButtonState.isLoading}
            >
              {saveButtonState.buttonText}
            </button>
                                    <button 
              className="submit-button" 
              onClick={submitForm}
              disabled={saveButtonState.isLoading || submitButtonState.isLoading}
            >
              {submitButtonState.buttonText}
                                    </button>
                                  </div>
          </>
        )}
      </footer>


      {formData.state_id === ResourceState.INACTIVE_IN_PROGRESS 
        || formData.state_id === ResourceState.INACTIVE_COMPLETED
        && (
          <div className="form-status-banner inactive">
            <span>This form is currently inactive</span>
            <button onClick={toggleFormActive}>Activate Form</button>
          </div>
        )}

      {/* Question Modal */}
      {modalState.isOpen && modalState.question && (
        <div className="question-modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget && !questionModalSaveState.isLoading) {
            closeQuestionModal();
          }
        }}>
          <div className="question-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="question-modal-header">
              <h2>{modalState.isNew ? 'Add Question' : 'Edit Question'}</h2>
                                  <button 
                className="question-modal-close"
                onClick={closeQuestionModal}
                disabled={questionModalSaveState.isLoading}
                                  >
                                    ×
                                  </button>
            </div>

            <div className="question-modal-body">
              {/* Description */}
              <div className="modal-form-group">
                <label>Description *</label>
                                        <input
                                          type="text"
                  value={modalState.question.description}
                  onChange={(e) => updateModalState({ question: { ...modalState.question!, description: e.target.value } })}
                  className={modalState.errors['description'] ? 'error' : ''}
                />
                {modalState.errors['description'] && (
                  <div className="error-message">{modalState.errors['description']}</div>
                )}
              </div>

              {/* Question Type */}
              <div className="modal-form-group modal-form-group-inline">
                <label>Evaluate Using</label>
                <div className="modal-selection-cards">
                  <button
                    type="button"
                    className={`modal-selection-card ${Number(modalState.question.type_id) === QuestionType.MOMENT ? 'selected' : ''}`}
                    onClick={() => {
                      const newTypeId = QuestionType.MOMENT;
                      // Reset options to two Yes/No options with Moment-specific names
                      const newOptions = [
                        { order: 1, points: 0, description: "Moment Expression is True" },
                        { order: 2, points: 0, description: "Moment Expression is False" }
                      ];
                      const updatedQuestion: EFQuestionData = {
                        ...modalState.question!,
                        type_id: newTypeId,
                        option_type_id: OptionType.YES_NO,
                        prompt: '',
                        examples: [],
                        options: newOptions,
                        moments: modalState.question!.moments && modalState.question!.moments.length > 0 ? modalState.question!.moments : [{ order: 1, moment_id: 0, operation_id: MomentOperation.NONE }],
                        order: modalState.question!.order || 1
                      };
                      
                      // If switching to MOMENT and no moments exist, initialize with one moment
                      if (!updatedQuestion.moments || updatedQuestion.moments.length === 0) {
                        updatedQuestion.moments = [{ order: 1, moment_id: 0, operation_id: MomentOperation.NONE }];
                      }
                      
                      updateModalState({ question: updatedQuestion });
                    }}
                  >
                    Moment
                  </button>
                                            <button
                                              type="button"
                    className={`modal-selection-card ${Number(modalState.question.type_id) === QuestionType.PROMPT ? 'selected' : ''}`}
                    onClick={() => {
                      const newTypeId = QuestionType.PROMPT;
                      // If switching to PROMPT and option type is YES_NO, reset options to prompt-specific names
                      let updatedOptions = modalState.question!.options || [];
                      if (Number(modalState.question!.option_type_id) === OptionType.YES_NO) {
                        updatedOptions = [
                          { order: 1, points: 0, description: "Yes or True" },
                          { order: 2, points: 0, description: "No or False" }
                        ];
                      }
                      const updatedQuestion: EFQuestionData = {
                        ...modalState.question!,
                        type_id: newTypeId,
                        prompt: modalState.question!.prompt || '',
                        examples: modalState.question!.examples || [],
                        moments: [],
                        options: updatedOptions,
                        order: modalState.question!.order || 1
                      };
                      updateModalState({ question: updatedQuestion });
                    }}
                  >
                    Prompt
                                            </button>
                                    </div>
                                </div>

              {/* Prompt Section - simplified for now */}
              {Number(modalState.question.type_id) === QuestionType.PROMPT && (
                <div className="modal-form-group">
                  <label>Prompt *</label>
                                  <textarea
                    value={modalState.question.prompt || ''}
                    onChange={(e) => updateModalState({ question: { ...modalState.question!, prompt: e.target.value } })}
                                    rows={3}
                    className={modalState.errors['prompt'] ? 'error' : ''}
                                  />
                  {modalState.errors['prompt'] && (
                    <div className="error-message">{modalState.errors['prompt']}</div>
                                )}
                        </div>
                      )}

              {/* Moments Section */}
              {Number(modalState.question.type_id) === QuestionType.MOMENT && (
                <div className="modal-form-group">
                  <label>Moments *</label>
                  {modalState.errors['moments'] && (
                    <div className="error-message">{modalState.errors['moments']}</div>
                  )}
                  <div className="modal-moments-container">
                    <div className="modal-moments-table">
                      <div className="modal-moments-header">
                        <div className="modal-moment-column">Category</div>
                        <div className="modal-moment-column">Moment</div>
                        <div className="modal-moment-column">Operation</div>
                        <div className="modal-moment-column">Actions</div>
                            </div>
                      <div className="modal-moments-list">
                        {modalState.question!.moments && modalState.question!.moments.map((moment, momentIndex) => {
                          const momentDetails = moments.find(m => m.id === moment.moment_id);
                                const momentCategoryId = momentDetails?.mmt_category_id || 0;
                          const selectedCategoryId = modalState.momentRowCategories[`modal-${momentIndex}`] || (momentCategoryId > 0 ? momentCategoryId : 0);
                          const isLastRow = modalState.question!.moments && modalState.question!.moments.length === 1;

                                return (
                            <div key={momentIndex} className="modal-moment-row">
                              <div className="modal-moment-column">
                                      <MomentCategoryDropdown
                                  value={selectedCategoryId}
                                        onChange={(categoryId, categoryName) => {
                                    updateModalMomentCategory(momentIndex, categoryId);
                                        }}
                                  disabled={false}
                                        emptyMessage="Select category"
                                  error={modalState.errors[`moment-${momentIndex}`] ? [modalState.errors[`moment-${momentIndex}`]] : undefined}
                                      />
                                    </div>
                              <div className="modal-moment-column">
                                          <AutoCompleteDropdown<MomentItem & { description: string }>
                                            apiEndpoint={api(PATH_SEGMENTS.MOMENT)}
                                            itemsKey="moments"
                                            additionalFilters={selectedCategoryId > 0 ? {
                                              mmt_category_id: selectedCategoryId
                                            } : undefined}
                                            value={moment.moment_id || 0}
                                            onChange={(momentId, momentName) => {
                                    updateModalMomentId(momentIndex, momentId);
                                            }}
                                  disabled={selectedCategoryId === 0}
                                            placeholder="Select a moment"
                                            emptyMessage={selectedCategoryId === 0 ? "First select a category" : "Select a moment"}
                                  error={modalState.errors[`moment-${momentIndex}`] ? [modalState.errors[`moment-${momentIndex}`]] : undefined}
                                          />
                                    </div>
                              <div className="modal-moment-column">
                                      <select 
                                  className={`form-select ${modalState.errors[`moment-${momentIndex}-operation`] ? 'error' : ''}`}
                                        value={moment.operation_id}
                                        onChange={(e) => {
                                          const operationId = parseInt(e.target.value);
                                    updateModalMomentOperation(momentIndex, operationId);
                                        }}
                                      >
                                        {(operations || []).map(operation => (
                                          <option key={operation.id} value={operation.id}>
                                            {operation.name}
                                          </option>
                                        ))}
                                      </select>
                                {modalState.errors[`moment-${momentIndex}-operation`] && (
                                  <div className="error-message">{modalState.errors[`moment-${momentIndex}-operation`]}</div>
                                      )}
                              </div>
                              <div className="modal-moment-column">
                                <div className="modal-moment-actions">
                                          <button 
                                    type="button"
                                            className="icon-button add" 
                                            title="Add a new moment after this one"
                                    onClick={() => addMomentToModal(momentIndex)}
                                          >
                                            <i className="fa fa-plus"></i>
                                          </button>
                                          <button 
                                    type="button"
                                            className="icon-button delete" 
                                            title="Remove this moment"
                                    onClick={() => removeMomentFromModal(momentIndex)}
                                            disabled={isLastRow}
                                          >
                                            <i className="fa fa-trash"></i>
                                          </button>
                                        </div>
                                    </div>
                                  </div>
                                );
                              })}
                      </div>
                            </div>
                            
                    {/* Moment Expression Display */}
                    {modalState.question.moments && modalState.question.moments.length > 0 && (
                      <div className="modal-moment-expression">
                        <label>Moment Expression</label>
                                <MomentExpression 
                          moments={modalState.question!.moments.map(moment => {
                                    const momentDetails = moments.find(m => m.id === moment.moment_id);
                                    const operationDetails = operations.find(o => o.id === moment.operation_id);
                                    return {
                                      id: moment.moment_id,
                                      name: momentDetails?.name || 'Undefined Moment',
                                      operation: operationDetails?.name || 'NONE'
                                    };
                                  })}
                                  onMomentClick={handleMomentClick}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

              {/* Options Section */}
              <div className="modal-form-group">
                {/* Option Type */}
                <div className="modal-form-group-inline with-margin">
                  <label>Options Type</label>
                  <div className="modal-selection-cards">
                    <button
                      type="button"
                      className={`modal-selection-card ${Number(modalState.question!.option_type_id) === OptionType.YES_NO ? 'selected' : ''} ${Number(modalState.question!.type_id) === QuestionType.MOMENT ? 'disabled' : ''}`}
                      onClick={() => {
                        if (Number(modalState.question!.type_id) === QuestionType.MOMENT) return;
                        const newOptionTypeId = OptionType.YES_NO;
                        // Set option names based on question type
                        const newOptions = [
                          { order: 1, points: 0, description: "Yes or True" },
                          { order: 2, points: 0, description: "No or False" }
                        ];
                        updateModalState({ question: { ...modalState.question!, option_type_id: newOptionTypeId, options: newOptions } });
                      }}
                      disabled={Number(modalState.question!.type_id) === QuestionType.MOMENT}
                    >
                      Yes / No
                    </button>
                    {Number(modalState.question.type_id) !== QuestionType.MOMENT && (
                      <>
                        <button
                          type="button"
                          className={`modal-selection-card ${Number(modalState.question.option_type_id) === OptionType.GRADING ? 'selected' : ''}`}
                          onClick={() => {
                            const newOptionTypeId = OptionType.GRADING;
                            let newOptions = modalState.question!.options || [];
                            if (newOptions.length === 0) {
                              newOptions = [{ order: 1, points: 0, description: "Option 1" }];
                            }
                            updateModalState({ question: { ...modalState.question!, option_type_id: newOptionTypeId, options: newOptions } });
                          }}
                        >
                          Grading
                        </button>
                        <button
                          type="button"
                          className={`modal-selection-card ${Number(modalState.question.option_type_id) === OptionType.MULTI_CHOICE ? 'selected' : ''}`}
                          onClick={() => {
                            const newOptionTypeId = OptionType.MULTI_CHOICE;
                            let newOptions = modalState.question!.options || [];
                            if (newOptions.length === 0) {
                              newOptions = [{ order: 1, points: 0, description: "Option 1" }];
                            }
                            updateModalState({ question: { ...modalState.question!, option_type_id: newOptionTypeId, options: newOptions } });
                          }}
                        >
                          MultiChoice
                        </button>
                      </>
                    )}
                        </div>
                </div>
                <label>Options</label>
                <div className="modal-options-container">
                  <div className="modal-options-table">
                    <div className="modal-options-header">
                      <div className="modal-option-column">Description</div>
                      <div className="modal-option-column">Points</div>
                      <div className="modal-option-column">Actions</div>
                    </div>
                    <div className="modal-options-list">
                      {modalState.question!.options && modalState.question!.options.map((option, optionIdx) => {
                        const isGradingType = Number(modalState.question!.option_type_id) === OptionType.GRADING;
                        const isYesNoType = Number(modalState.question!.option_type_id) === OptionType.YES_NO;
                        const isLastOption = (modalState.question!.options?.length || 0) === 1;
                        const isMomentType = Number(modalState.question!.type_id) === QuestionType.MOMENT;
                            
                            return (
                          <div key={optionIdx} className="modal-option-row">
                            <div className="modal-option-column">
                                  <input
                                    type="text"
                                value={option.description || ''}
                                onChange={(e) => updateModalOptionDescription(optionIdx, e.target.value)}
                                disabled={isYesNoType}
                                className={modalState.errors[`option-${optionIdx}-description`] ? 'error' : ''}
                              />
                              {modalState.errors[`option-${optionIdx}-description`] && (
                                <div className="error-message">{modalState.errors[`option-${optionIdx}-description`]}</div>
                              )}
                                </div>
                            <div className="modal-option-column">
                                  <input 
                                    type="number" 
                                    value={option.points} 
                                onChange={(e) => updateModalOptionPoints(optionIdx, parseInt(e.target.value) || 0)}
                                  />
                                </div>
                            <div className="modal-option-column">
                              {!isYesNoType && (
                                <div className="modal-option-actions">
                                      <button 
                                    type="button"
                                        className="icon-button add" 
                                        title="Add option after this one"
                                    onClick={() => addOptionToModal(optionIdx)}
                                      >
                                        <i className="fa fa-plus"></i>
                                      </button>
                                      <button 
                                    type="button"
                                        className="icon-button delete" 
                                        title="Delete option"
                                    onClick={() => removeOptionFromModal(optionIdx)}
                                        disabled={isLastOption}
                                      >
                                        <i className="fa fa-trash"></i>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                  {Number(modalState.question.option_type_id) !== OptionType.YES_NO && (
            <button 
              type="button"
                      className="modal-add-option-button"
                      onClick={() => addOptionToModal(null)}
            >
                      <i className="fa fa-plus"></i> Add Option
            </button>
          )}
        </div>
          </div>
                </div>

            <div className="question-modal-footer">
            <button 
                type="button"
                className="cancel-button"
                onClick={closeQuestionModal}
                disabled={questionModalSaveState.isLoading}
              >
                Cancel
            </button>
            <button 
                type="button"
                className="save-button"
                onClick={saveQuestionFromModal}
                disabled={questionModalSaveState.isLoading}
              >
                {questionModalSaveState.buttonText}
            </button>
            </div>
          </div>
          </div>
        )}

      {/* Section Modal (for both Add and Duplicate) */}
      {sectionModal.isOpen && (
        <div className="question-modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget && !sectionModalSaveState.isLoading) {
            closeSectionModal();
          }
        }}>
          <div className="question-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="question-modal-header">
              <h2>{sectionModal.mode === 'duplicate' ? 'Duplicate Section' : 'Add Section'}</h2>
              <button 
                className="question-modal-close"
                onClick={closeSectionModal}
                disabled={sectionModalSaveState.isLoading}
              >
                ×
              </button>
            </div>

            <div className="question-modal-body">
              <div className="modal-form-group">
                <label>Section Name *</label>
                <input
                  type="text"
                  value={sectionModal.sectionName}
                  onChange={(e) => handleSectionModalNameChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveSection();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      closeSectionModal();
                    }
                  }}
                  className={sectionModal.errors['name'] ? 'error' : ''}
                  autoFocus
                  placeholder="Enter section name"
                />
                {sectionModal.errors['name'] && (
                  <div className="error-message">{sectionModal.errors['name']}</div>
                )}
              </div>
            </div>

            <div className="question-modal-footer">
              <button 
                type="button"
                className="cancel-button"
                onClick={closeSectionModal}
                disabled={sectionModalSaveState.isLoading}
              >
                Cancel
              </button>
              <button 
                type="button"
                className="save-button"
                onClick={saveSection}
                disabled={sectionModalSaveState.isLoading}
              >
                {sectionModalSaveState.buttonText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant={confirmationModal.variant}
        onConfirm={confirmationModal.onConfirm}
        onCancel={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default EvaluationForm; 